const setToken = ({ access, refresh }) => {
  if (access) {
    localStorage.setItem("access_token", access);
  }
  if (refresh) {
    localStorage.setItem("refresh_token", refresh);
  }
};

const getAccessToken = () => localStorage.getItem("access_token");

const getRefreshToken = () => localStorage.getItem("refresh_token");

const getUserId = () => localStorage.getItem("user_id");

const setUserId = (userId) => localStorage.setItem("user_id", userId);

const readJwtUserId = () => {
  try {
    const token = localStorage.getItem("access_token");
    const segment = String(token || "").split(".")[1];
    if (!segment) return null;
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload.user_id != null ? String(payload.user_id) : null;
  } catch {
    return null;
  }
};

const getClientUserId = () => {
  const value = localStorage.getItem("client_user_id");
  // Guard against historical bug that wrote the string "undefined"
  // when login used response.client.user.id (client payload has no nested user).
  if (value && value !== "undefined" && value !== "null") {
    return value;
  }
  const fromJwt = readJwtUserId();
  if (fromJwt) {
    localStorage.setItem("client_user_id", fromJwt);
    return fromJwt;
  }
  return null;
};

const setClientUserId = (userId) => {
  if (userId == null || userId === "undefined" || userId === "null") {
    localStorage.removeItem("client_user_id");
    return;
  }
  localStorage.setItem("client_user_id", userId);
};

const getUrlSuffix = () => localStorage.getItem("url_suffix");

const setUrlSuffix = (urlSuffix) => localStorage.setItem("url_suffix", urlSuffix);

const getTalentOnboarded = () => localStorage.getItem("talent_onboarded") === "true";

const setTalentOnboarded = (onboarded) => localStorage.setItem("talent_onboarded", onboarded);

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

const setUser = (user) => localStorage.setItem("user", JSON.stringify(user));

const setAdminToken = ({ access, refresh }) => {
  if (access) {
    localStorage.setItem("admin_access_token", access);
  }
  if (refresh) {
    localStorage.setItem("admin_refresh_token", refresh);
  }
};

const getAdminAccessToken = () => localStorage.getItem("admin_access_token");

const getAdminRefreshToken = () => localStorage.getItem("admin_refresh_token");

const getAdminUserId = () => localStorage.getItem("admin_user_id");

const setAdminUserId = (userId) =>
  localStorage.setItem("admin_user_id", userId);

const getAdminUser = () => {
  try {
    return JSON.parse(localStorage.getItem("admin_user"));
  } catch {
    return null;
  }
};

const setAdminUser = (user) =>
  localStorage.setItem("admin_user", JSON.stringify(user));

const getAdminGroups = () => {
  const groups = localStorage.getItem("admin_user_groups");
  if (!groups) return [];
  try {
    const parsed = JSON.parse(groups);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error parsing admin_user_groups:', error);
    return [];
  }
};

const getAdminGroupIds = () => {
  const groupIds = localStorage.getItem("admin_user_group_ids");
  if (!groupIds) return '';
  try {
    const parsed = JSON.parse(groupIds);
    return Array.isArray(parsed) ? parsed.join(',') : '';
  } catch (error) {
    console.error('Error parsing admin_user_group_ids:', error);
    return '';
  }
};

const setAdminGroupIds = (groupIds) =>
  localStorage.setItem("admin_user_group_ids", JSON.stringify(groupIds));

const setAdminGroups = (groups) =>
  localStorage.setItem("admin_user_groups", JSON.stringify(groups));

const saveLoginToken = (response) => {
  if (response.user) {
    setAdminToken(response);
    setAdminUser(response.user);
    setAdminUserId(response.user.id);
    setAdminGroups(response.user.groups);
    setAdminGroupIds(response.user.group_ids)
  }
  if (response.talent) {
    setToken(response);
    setUserId(response.talent.user.id);
    setTalentOnboarded(!!response.talent.skills_survey_submitted_at);
    setUrlSuffix(response.talent.url_suffix);
  }
  if (response.client) {
    setToken(response);
    // Login returns UserSerializer at response.client (flat id), not talent's nested .user
    setClientUserId(response.client?.id ?? response.client?.user?.id);
  }
};

const authKeys = [
  "access_token",
  "refresh_token",
  "user_id",
  "user",
  "url_suffix",
  "talent_onboarded",
  "admin_access_token",
  "admin_refresh_token",
  "admin_user_id",
  "admin_user",
  "admin_user_groups",
  "admin_user_group_ids",
  "client_user_id",
];

const impersonatePrefix = "impersonator__";
const refreshLockKeys = ["admin_token_refresh_lock", "token_refresh_lock"];

const clearLoginToken = () => {
  for (const key of authKeys) {
    localStorage.removeItem(key);
    localStorage.removeItem(`${impersonatePrefix}${key}`);
  }
  for (const key of refreshLockKeys) {
    localStorage.removeItem(key);
  }
};

const impersonateStart = (response) => {
  for (const key of authKeys) {
    const value = localStorage.getItem(key);
    if (value) {
      localStorage.setItem(`${impersonatePrefix}${key}`, value);
      localStorage.removeItem(key);
    }
  }
  saveLoginToken(response);
};

const impersonateStop = () => {
  for (const key of authKeys) {
    localStorage.removeItem(key);
    const impersonateKey = `${impersonatePrefix}${key}`;
    const value = localStorage.getItem(impersonateKey);
    if (value) {
      localStorage.setItem(key, value);
      localStorage.removeItem(impersonateKey);
    }
  }
};

const getImpersonatorAdminToken = () => localStorage.getItem(`${impersonatePrefix}admin_access_token`);


export {
  setToken,
  getAccessToken,
  getRefreshToken,
  getUserId,
  setUserId,
  getClientUserId,
  setClientUserId,
  getUrlSuffix,
  setUrlSuffix,
  getUser,
  setUser,
  setAdminToken,
  getAdminAccessToken,
  getAdminRefreshToken,
  getAdminUserId,
  setAdminUserId,
  getAdminUser,
  setAdminUser,
  getAdminGroups,
  setAdminGroups,
  getAdminGroupIds,
  setAdminGroupIds,
  getTalentOnboarded,
  setTalentOnboarded,
  saveLoginToken,
  clearLoginToken,
  impersonateStart,
  impersonateStop,
  getImpersonatorAdminToken,
};
